import React from "react"
import styled from "styled-components"
import { AppFunctionComponent } from "../../../../../types"
import { TimelineConfig } from "./timeline.interface"
import {
  BackgroundWrapper,
  SectionHeading,
  SectionLayout,
} from "../../section.component"
import { SectionContainer } from "../../../../../resolvers/text/text.resolver"

type TimelineProps = {
  config: TimelineConfig
}

const TimelineContainer = styled(SectionContainer)`
  width: 100%;
  padding: 0 0px;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
  }

  ${SectionHeading} {
    width: 100%;
    max-width: 100%;
    margin-bottom: 60px;

    h2 {
      width: 100%;
      text-align: left !important;

      /* Heading/H2 */
      font-family: Facto;
      font-size: 23px;
      font-style: normal;
      font-weight: 500;
      line-height: 120%; /* 27.6px */

      @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
        font-family: Facto;
        font-size: 23px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }
    }
  }
`

const TimelineList = styled.div`
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 10px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #e5e5e5;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    margin-left: 20px;
  }
`

const TimelineItemWrapper = styled.div`
  position: relative;
  padding-left: 64px;
  margin-bottom: 60px;
  text-align: left;

  &::before {
    content: "";
    position: absolute;
    left: 1px;
    top: 8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #000;
    z-index: 1;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const TimelineTitle = styled.h3`
  font-family: Facto;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%; /* 33.6px */
  margin: 0 0 32px 0;
  color: #000;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
  }
`

const TimelineDescription = styled.p`
  font-family: ${({ theme }) => theme.font.basic};
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 16px 0;
  color: #666;
`

const TimelineFeatures = styled.div`
  margin-top: 16px;
`

const FeatureCategory = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`

const FeatureCategoryTitle = styled.h4`
  font-family: ${({ theme }) => theme.font.basic};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0 0 8px 0;
  color: #666;
`

const FeatureList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style: none;

  li {
    position: relative;
    font-family: ${({ theme }) => theme.font.basic};
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 4px;
    color: #666;

    &::before {
      content: "•";
      position: absolute;
      left: -12px;
      color: #666;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const Timeline: AppFunctionComponent<TimelineProps> = ({ config }) => {
  const parseFeatures = (features: string[]) => {
    const parsed: { [key: string]: string[] } = {}
    let currentCategory = "default"

    features.forEach((feature) => {
      // Check if this is a category (doesn't start with bullet point)
      if (
        !feature.startsWith("•") &&
        !feature.startsWith("-") &&
        feature.endsWith(":")
      ) {
        currentCategory = feature.replace(":", "")
        parsed[currentCategory] = []
      } else {
        // This is a feature item
        if (!parsed[currentCategory]) {
          parsed[currentCategory] = []
        }
        // Remove bullet points if they exist
        const cleanFeature = feature.replace(/^[•\-\s]+/, "")
        parsed[currentCategory].push(cleanFeature)
      }
    })

    return parsed
  }

  return (
    <BackgroundWrapper background={config.timeline.background}>
      <TimelineContainer layout={SectionLayout.TextLeftAligned}>
        <SectionHeading style={{ textAlign: "left" }}>
          <h2>{config.timeline.heading}</h2>
        </SectionHeading>

        <TimelineList>
          {config.timeline.items.map((item, index) => (
            <TimelineItemWrapper key={index}>
              <TimelineTitle>{item.title}</TimelineTitle>
              {item.description && (
                <TimelineDescription>{item.description}</TimelineDescription>
              )}
              {item.features && item.features.length > 0 && (
                <TimelineFeatures>
                  {(() => {
                    const parsedFeatures = parseFeatures(item.features)
                    return Object.entries(parsedFeatures).map(
                      ([category, features]) => (
                        <FeatureCategory key={category}>
                          {category !== "default" && (
                            <FeatureCategoryTitle>
                              {category}
                            </FeatureCategoryTitle>
                          )}
                          <FeatureList>
                            {features.map((feature, featureIndex) => (
                              <li key={featureIndex}>{feature}</li>
                            ))}
                          </FeatureList>
                        </FeatureCategory>
                      )
                    )
                  })()}
                </TimelineFeatures>
              )}
            </TimelineItemWrapper>
          ))}
        </TimelineList>
      </TimelineContainer>
    </BackgroundWrapper>
  )
}

export default Timeline
